import React from 'react';
import { useLocale } from 'locale';
import { useDefaultAgentCoverImageUrl } from 'hooks/useDefaultAgentCoverImageUrl';
import { ImageOptions, useImageUrlBuilder } from 'hooks/useImageUrlBuilder';
import { Root, AgencyName } from './styled';
import {
  CardCover,
  AvatarPosition,
  CardUpperSection,
  CardContentSection,
  CardContentDetails,
  TopTenBadgePosition,
  ContactButton,
} from '../styled';
import { TopTenBadgeVariant, TopTenBadge } from '../top-ten-badge';
import { AgencyAvatar } from './AgencyAvatar';
import PhoneAnchor from '../phone-anchor/PhoneAnchor';
import VerifiedIcon from 'assets/svg/verified.svg';
import { noopKeypress } from '../utils';
import { SpacingBlock } from 'components/spacing-block';
import { PropertiesSummary } from '../properties-summary';


const defaultCoverImageOptions: ImageOptions = { width: 264, height: 92 };

export interface AgencyCardProps {
  id: string;
  isRegistered?: boolean;
  agencyName: string;
  avatarImage?: string;
  coverImage?: string;
  soldCount: number;
  exclusiveListingsCount: number;
  topTenBadgeVariant?: TopTenBadgeVariant;
  onContactClick: (id: string) => void;
  phone?: string;
  enablePhoneShowing?: boolean;
  verifiedIconColor?: string;
  withPropertyInfo?: boolean;
  variantToImagePath?: Record<TopTenBadgeVariant, string>;
}

export const AgencyCard: React.FC<AgencyCardProps> = React.memo(props => {
  const {
    id,
    isRegistered = true,
    agencyName,
    avatarImage,
    coverImage,
    topTenBadgeVariant,
    onContactClick,
    phone,
    enablePhoneShowing,
    verifiedIconColor,
    soldCount,
    exclusiveListingsCount,
    withPropertyInfo = false,
    variantToImagePath,
  } = props;
  const { t } = useLocale();
  const constructImageURI = useImageUrlBuilder();
  const defaultCoverImageUrl = useDefaultAgentCoverImageUrl(defaultCoverImageOptions);
  const coverImageUrl = coverImage ? constructImageURI(coverImage, defaultCoverImageOptions) : null;
  const avatarImageUrl = avatarImage ? constructImageURI(avatarImage) : null;

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onContactClick(id);
  };

  return (
    <Root isRegistered={isRegistered}>
      <CardUpperSection>
        <CardCover imgUrl={isRegistered ? (coverImageUrl || defaultCoverImageUrl) : null} />
        <AvatarPosition>
          <AgencyAvatar imageUrl={isRegistered ? avatarImageUrl : null} />
        </AvatarPosition>
        {topTenBadgeVariant ? (
          <TopTenBadgePosition>
            <TopTenBadge variant={topTenBadgeVariant} withShadow={isRegistered} variantToImagePath={variantToImagePath} />
          </TopTenBadgePosition>
        ) : null}
      </CardUpperSection>
      <CardContentSection>
        <CardContentDetails>
          <AgencyName
            weight="bold"
            onKeyPress={noopKeypress}
            isRegistered={isRegistered}
            verifiedIconColor={verifiedIconColor}
          >
            <h3>{agencyName}</h3>
            {isRegistered ? <VerifiedIcon /> : null}
          </AgencyName>
          {withPropertyInfo ? (
            <SpacingBlock mTop={1}>
              <PropertiesSummary
                soldCount={soldCount}
                exclusiveListingsCount={exclusiveListingsCount}
              />
            </SpacingBlock>
          ) : null}
        </CardContentDetails>
        {isRegistered ? (
          enablePhoneShowing ? (
            <PhoneAnchor
              dataAuto="contact-agency-button"
              phone={phone}
              label={t('madad.contactAgency')}
              officeId={id}
            />
          ) : (
            <ContactButton data-auto="contact-agency-button" onClick={handleContactClick}>
              {t('madad.contactAgency')}
            </ContactButton>
          )
        ) : null}
      </CardContentSection>
    </Root>
  );
});
